import { ReactNode, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { HostPortalContext } from "@/contexts/portalBranding/context";
import { suspendPortalTheme } from "@/theme/brandTheme";
import { useFavicon } from "@/lib/favicon";
import reveshareIcon from "../../assets/logo.svg";

interface UnbrandedSurfaceProps {
  /** Document title for the surface, e.g. "Reveshare Admin". */
  title: string;
  children: ReactNode;
}

/**
 * Renders a subtree as Reveshare, even on a brand's white-label portal host.
 *
 * The admin and sales panels are Reveshare's own tools: an operator who
 * happens to open them on `<brand>.reveshare.com` must not see the brand's
 * logo, name, colours or favicon. Three things make a host branded, and this
 * turns off all three for its children:
 *
 *  - identity: the host branding context is replaced with the unbranded
 *    default, so usePortalBranding (logo mark, headings) answers "Reveshare";
 *  - colours: the portal theme layer is suspended while mounted, so the
 *    stylesheet's default scale applies;
 *  - head: the title and favicon are set from here, which wins over the
 *    host-level head because Helmet and useFavicon both let the deepest
 *    mount decide.
 */
export function UnbrandedSurface({ title, children }: UnbrandedSurfaceProps) {
  useEffect(() => suspendPortalTheme(), []);
  useFavicon(reveshareIcon);

  return (
    <HostPortalContext.Provider
      value={{
        isLoading: false,
        branding: null,
        isUnknownPortal: false,
        unknownSlug: null,
      }}
    >
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {children}
    </HostPortalContext.Provider>
  );
}
